import clsx from 'clsx';
import React from 'react';
import type { Key } from 'react-aria-components';
import { ToggleButton, ToggleButtonGroup } from 'react-aria-components';

import { focusRingClassName } from '#/shared/utils/domHelper';

import { MaterialIcon } from './MaterialIcon';

type ButtonGroupOption = {
  label: string;
  icon?: string;
  selectedClassName?: string;
  id: string;
};

type ButtonGroupProps = {
  options: ButtonGroupOption[];
  className?: string;
  value?: string;
  onChange?: (value: string | undefined) => void;
};

export function ButtonGroup({ options, className, value, onChange }: ButtonGroupProps) {
  const selectedKeys = React.useMemo(() => {
    const keys = new Set<Key>();
    if (value !== undefined) {
      keys.add(value);
    }
    return keys;
  }, [value]);

  const handleSelectionChange = (keys: Set<Key>) => {
    const selectedKey = Array.from(keys)[0] as string | undefined;
    if (selectedKey === undefined) {
      onChange?.(undefined);
      return;
    }

    onChange?.(selectedKey);
  };

  return (
    <ToggleButtonGroup
      selectionMode="single"
      selectedKeys={selectedKeys}
      onSelectionChange={handleSelectionChange}
      className={clsx('flex rounded-sm border-1 border-primary', className)}
    >
      {options.map((option, index) => {
        const isFirst = index === 0;
        const isLast = index === options.length - 1;

        return (
          <ToggleButton
            key={option.id}
            id={option.id}
            className={({ isSelected }) =>
              clsx(
                'h-[42px] min-w-[90px] px-12 py-0 flex items-center justify-center gap-1 transition-all',
                'text-center whitespace-nowrap',
                'w-full',
                'm-0',
                'cursor-pointer',
                `${focusRingClassName} focus-visible:z-0 focus-visible:border-none`,
                !isLast && 'border-r border-primary border-r-[1px]',
                isFirst && 'rounded-l-sm',
                isLast && 'rounded-r-sm',
                isSelected
                  ? option.selectedClassName || 'bg-surface-l2 text-primary'
                  : 'bg-surface-l0 text-primary hover:bg-surface-l2',
              )
            }
          >
            {({ isSelected }) =>
              option.icon && isSelected ? (
                <>
                  <MaterialIcon iconName={option.icon} size="sm" />
                  {option.label}
                </>
              ) : (
                option.label
              )
            }
          </ToggleButton>
        );
      })}
    </ToggleButtonGroup>
  );
}
